<script setup lang="ts">
/**
 * One execution in Work's merged list (#1747, N8 of #1697): what its board card says, on one row, opening in place
 * into its runs (its steps). The kit Card is the row, the kit Collapsible holds the runs, and the pills are the
 * same `ExecutionStatus` the board and the run list draw, so the row states no surface or colour of its own.
 *
 * The runs come from the execution detail read that already exists, and only for a row somebody opened: the
 * board is polled every three seconds, and a detail read per row on every poll would cost far more than the view
 * is worth (CTO decision 6). They are read on the first open and read again only when this execution's summary on
 * the board says they may have moved: its run count, its status or its finish time. Until then a second open shows
 * what was read. A row that is closed when its summary moves reads again the next time it opens, or at once if a
 * read is still out, so that read never lands under the old summary.
 *
 * No ID is shown, open or closed (CTO decision 8): the execution and each run are named by what they did, and the
 * links carry the IDs for anybody who needs the old pages.
 *
 * The row leads with what the execution is doing now: its current step, when it has one, over the name it started
 * under. Many executions start on the same task ("Ask CEO to go live (merge)"), so by name alone their rows read the
 * same and a step taken inside one changes nothing a reader can see. Its resources sit beside the title by name and
 * the last part of their address ("Pull request #91"), which tells those rows apart.
 */
import { ArrowRight } from '@lucide/vue'
import {
    Button,
    Card,
    CardContent,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    ZhEmptyState,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onUnmounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
    api,
    type ExecutionResource,
    type ExecutionRun,
    type ExecutionSummary,
    formatAgo,
    formatDuration,
    runStoppedLine,
} from '@/api'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { executionPillStatus, runPillStatus } from '@/lib/statusTone'

const props = defineProps<{ execution: ExecutionSummary; now: number }>()

/** Not remembered between visits, as the sidebar's places are not (#1744, CTO decision 7). */
const open = ref(false)
/** The runs last read, earliest first; null until the first read lands. */
const runs = ref<ExecutionRun[] | null>(null)
const readFailed = ref(false)
const reading = ref(false)

/** What on the board says this execution's runs may have changed (CTO decision 6). */
const summary = computed(() =>
    [props.execution.runCount, props.execution.status, props.execution.finishedAt ?? ''].join('|'),
)
/** The summary the runs on hand were read under: a different one means they may be out of date. */
let readUnder: string | null = null
let request: AbortController | null = null

function startedAt(run: ExecutionRun): number {
    return new Date(run.startedAt ?? run.createdAt).getTime()
}

/** Earliest first, as the steps happened; a run not yet started sorts by when it was queued. */
function inOrder(list: ExecutionRun[]): ExecutionRun[] {
    return [...list].sort((a, b) => startedAt(a) - startedAt(b))
}

/** Read this execution's runs, dropping a read already in flight so an older answer never lands over a newer one. */
async function readRuns(): Promise<void> {
    request?.abort()
    const current = new AbortController()
    request = current
    const under = summary.value
    reading.value = true
    readFailed.value = false
    try {
        const detail = await api.execution(props.execution.id, current.signal)
        if (request !== current) return
        runs.value = inOrder(detail.runs)
        readUnder = under
    } catch {
        if (request !== current) return
        readFailed.value = true
    } finally {
        if (request === current) {
            request = null
            reading.value = false
        }
    }
}

watch(open, isOpen => {
    if (isOpen && !reading.value && (runs.value === null || readUnder !== summary.value)) void readRuns()
})

/**
 * An open row reads again at once, dropping a read made under the old summary. A closed one waits for its next open,
 * unless a read is still out: that read would land under the old summary, and the `open` watcher would not replace
 * it on a reopen while it is in flight, so it is replaced now.
 */
watch(summary, () => {
    if (open.value || request) void readRuns()
})

onUnmounted(() => {
    request?.abort()
    request = null
})

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`
const counts = computed(
    () => `${plural(props.execution.runCount, 'run')} \u00b7 ${plural(props.execution.taskCount, 'task')}`,
)
const currentTasks = computed(() => props.execution.currentTasks.map(task => task.name).join(' \u00b7 '))
/** The current step when there is one, else the name the execution started under (or was given). */
const heading = computed(() => currentTasks.value || props.execution.name)

/**
 * A resource by its name and the last part of its address: `Pull request #91`, `Ticket ABC-12`. Any host, no host's
 * own path shape: a numeric last part reads as a number, a short other one as it is, and a long one is left off.
 */
function resourceLabel(resource: ExecutionResource): string {
    let tail = ''
    try {
        tail = new URL(resource.url).pathname.split('/').filter(Boolean).at(-1) ?? ''
    } catch {
        return resource.name
    }
    if (!tail || tail.length > 24) return resource.name
    return /^\d+$/.test(tail) ? `${resource.name} #${tail}` : `${resource.name} ${tail}`
}
const resources = computed(() =>
    props.execution.resources.slice(0, 2).map(resource => ({ url: resource.url, label: resourceLabel(resource) })),
)
const duration = computed(() =>
    formatDuration(
        Math.max(
            0,
            (props.execution.finishedAt ? new Date(props.execution.finishedAt).getTime() : props.now) -
                new Date(props.execution.startedAt).getTime(),
        ),
    ),
)
const ago = (at: string) => formatAgo(Math.max(0, props.now - new Date(at).getTime()))
</script>

<template>
    <Card :data-execution-id="execution.id" class="text-sm">
        <Collapsible v-model:open="open">
            <CardContent>
                <div class="flex flex-wrap items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <RouterLink :to="`/executions/${execution.id}`" class="hover:text-primary font-medium">{{ heading }}</RouterLink>
                            <a v-for="resource in resources" :key="resource.url" :href="resource.url" target="_blank" rel="noopener noreferrer" class="text-muted-foreground hover:text-primary text-xs whitespace-nowrap">{{ resource.label }}</a>
                        </div>
                        <p v-if="currentTasks" class="text-muted-foreground mt-1 text-xs">{{ execution.name }}</p>
                    </div>
                    <div class="flex items-center gap-2">
                        <ExecutionStatus :status="executionPillStatus(execution)" />
                        <!-- A link, drawn as the kit icon Button: the RouterLink is the element, the Button its look. -->
                        <Button as-child variant="ghost" size="icon-xs">
                            <RouterLink :to="`/executions/${execution.id}`" :aria-label="`Open ${execution.name}`"><ArrowRight :size="14" /></RouterLink>
                        </Button>
                    </div>
                </div>
                <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span class="flex items-center gap-2">
                        <span v-if="execution.members.length" class="flex gap-1"><MemberAvatar v-for="member in execution.members.slice(0, 3)" :key="member.id" :member="member" class="size-6" fallback-class="text-xs" /></span>
                        <span>{{ execution.members.map(member => member.name).join(', ') || 'OS' }}</span>
                    </span>
                    <span class="text-muted-foreground whitespace-nowrap tabular-nums">Active {{ ago(execution.updatedAt || execution.startedAt) }}</span>
                    <span class="text-muted-foreground whitespace-nowrap tabular-nums">Started {{ ago(execution.startedAt) }}</span>
                    <span class="text-muted-foreground whitespace-nowrap tabular-nums">{{ duration }}</span>
                    <span class="text-muted-foreground whitespace-nowrap tabular-nums">{{ counts }}</span>
                </div>
                <CollapsibleTrigger class="mt-2" :aria-label="`Runs of ${execution.name}`">Runs</CollapsibleTrigger>
                <CollapsibleContent>
                    <div class="space-y-2 pt-1">
                        <div v-if="readFailed" role="alert" class="text-destructive flex flex-wrap items-center justify-between gap-3 text-xs">
                            <span>Could not load the runs of this execution.</span>
                            <Button variant="outline" size="sm" :disabled="reading" @click="readRuns">Try again</Button>
                        </div>
                        <p v-if="runs === null && !readFailed" role="status" class="text-muted-foreground text-xs">Loading runs…</p>
                        <ZhEmptyState v-else-if="runs !== null && runs.length === 0" compact>No runs in this execution yet.</ZhEmptyState>
                        <ol v-else-if="runs !== null" class="space-y-2">
                            <li v-for="run in runs" :key="run.id" class="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <RouterLink :to="`/runs/${run.id}`" class="hover:text-primary min-w-0 flex-1 font-medium">{{ run.task.name }}</RouterLink>
                                <ZhTooltip :text="runStoppedLine(run)"><ExecutionStatus :status="runPillStatus(run)" /></ZhTooltip>
                                <span v-if="run.member" class="flex items-center gap-1.5 text-xs"><MemberAvatar :member="run.member" class="size-5" fallback-class="text-xs" />{{ run.member.name }}</span>
                                <span v-if="run.startedAt" class="text-muted-foreground text-xs whitespace-nowrap tabular-nums">Started {{ ago(run.startedAt) }}</span>
                            </li>
                        </ol>
                    </div>
                </CollapsibleContent>
            </CardContent>
        </Collapsible>
    </Card>
</template>
